import React, { useEffect } from "react";
import { View, Text, TouchableOpacity, Image } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import { MaterialIcons } from "@expo/vector-icons";
import { router, usePathname } from "expo-router";
import { useDispatch, useSelector } from "react-redux";
import { appActions } from "../../store/slices/appSlice";
import { RootState } from "../../store";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withSpring,
  interpolate,
} from "react-native-reanimated";
import NotificationBell from "../notifications/NotificationBell";

interface StandardNavigationProps {
  title?: string;
  showBackButton?: boolean;
  showMenuButton?: boolean;
  showLogo?: boolean;
  customTitle?: React.ReactNode;
  icon?: React.ReactNode | null;
  shareRoute?: boolean;
}

const StandardNavigation: React.FC<StandardNavigationProps> = ({
  title,
  showBackButton = true,
  showMenuButton = true,
  showLogo = true,
  customTitle,
  icon = null,
  shareRoute = false,
}) => {
  const path = usePathname();
  const dispatch = useDispatch();
  const { isDrawerOpen } = useSelector((state: RootState) => state.app);
  const burgerAnim = useSharedValue(0);

  useEffect(() => {
    burgerAnim.value = withSpring(isDrawerOpen ? 1 : 0, {
      damping: 15,
      stiffness: 120,
    });
  }, [isDrawerOpen]);

  const topBarStyle = useAnimatedStyle(() => ({
    transform: [
      { translateY: interpolate(burgerAnim.value, [0, 1], [0, 7]) },
      { rotate: `${interpolate(burgerAnim.value, [0, 1], [0, 45])}deg` },
    ],
  }));

  const middleBarStyle = useAnimatedStyle(() => ({
    opacity: interpolate(burgerAnim.value, [0, 0.3], [1, 0]),
    transform: [{ scaleX: interpolate(burgerAnim.value, [0, 0.3], [1, 0]) }],
  }));

  const bottomBarStyle = useAnimatedStyle(() => ({
    transform: [
      { translateY: interpolate(burgerAnim.value, [0, 1], [0, -7]) },
      { rotate: `${interpolate(burgerAnim.value, [0, 1], [0, -45])}deg` },
    ],
  }));

  const handleMenuPress = () => {
    dispatch(appActions.toggleDrawer());
  };

  return (
    <LinearGradient
      colors={["rgba(19, 121, 162, 1)", "rgba(5, 150, 208, 1)"]}
      style={{
        paddingTop: 5,
        paddingBottom: 5,
        paddingHorizontal: 20,
      }}
    >
      {/* Navigation Bar */}
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: title || customTitle ? 10 : 0,
        }}
      >
        {/* Back Button shouldnt be in start page*/}
        {showBackButton && path !== "/(drawer)/start" && path !== "/start" ? (
          <TouchableOpacity
            onPress={() =>
              !shareRoute ? router.back() : router.push("/(drawer)/start")
            }
            style={{
              flexDirection: "row",
              alignItems: "center",
              minWidth: 40,
            }}
          >
            <MaterialIcons name="arrow-back" size={28} color="white" />
          </TouchableOpacity>
        ) : (
          <View style={{ minWidth: 40 }} />
        )}

        {/* Center Logo */}
        {showLogo && (
          <TouchableOpacity
            onPress={() => router.push("/(drawer)/start")}
            style={{ flex: 1, alignItems: "center" }}
          >
            <Image
              source={require("../../assets/homePage/logo-white.png")}
              style={{ width: 40, height: 40 }}
              resizeMode="contain"
            />
          </TouchableOpacity>
        )}

        {/* Menu Button */}
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <NotificationBell />
          {showMenuButton ? (
          <TouchableOpacity
            onPress={handleMenuPress}
            style={{
              minWidth: 40,
              alignItems: "flex-end",
              padding: 5,
            }}
            activeOpacity={0.7}
          >
            <View style={{ width: 25, height: 17, justifyContent: "center" }}>
              <Animated.View
                style={[
                  {
                    position: "absolute",
                    top: 0,
                    width: 25,
                    height: 3,
                    backgroundColor: "white",
                    borderRadius: 2,
                  },
                  topBarStyle,
                ]}
              />
              <Animated.View
                style={[
                  {
                    position: "absolute",
                    top: 7,
                    width: 25,
                    height: 3,
                    backgroundColor: "white",
                    borderRadius: 2,
                  },
                  middleBarStyle,
                ]}
              />
              <Animated.View
                style={[
                  {
                    position: "absolute",
                    top: 14,
                    width: 25,
                    height: 3,
                    backgroundColor: "white",
                    borderRadius: 2,
                  },
                  bottomBarStyle,
                ]}
              />
            </View>
          </TouchableOpacity>
        ) : (
          <View style={{ minWidth: 40 }} />
        )}
        </View>
      </View>

      {/* Title Section */}
      {(title || customTitle) && (
        <View
          style={{
            paddingHorizontal: 0,
            paddingVertical: 10,
            flexDirection: "row-reverse",

            alignItems: "center",
            // justifyContent: "center",
          }}
        >
          {icon && (
            <View style={{ marginRight: 10 }}>
              {React.isValidElement(icon) ? icon : null}
            </View>
          )}
          {customTitle || (
            <Text
              style={{
                color: "white",
                fontSize: 18,
                fontFamily: "kawkabBold",
                textAlign: "center",
                marginHorizontal: 10,
              }}
            >
              {title}
            </Text>
          )}
        </View>
      )}
    </LinearGradient>
  );
};

export default StandardNavigation;
